/** Keep this portable implementation identical in web and mobile. */
import "symbol-observable";

import {
	Actions,
	CoreActions,
	coreReducer,
	initState,
	JsonFormsCore,
	Middleware,
	UPDATE_DATA
} from "@jsonforms/core";
import isEqual from "lodash/isEqual";

import {
	AlternativeContext,
	AlternativeState
} from "./AlternativeContext";
import {
	getAlternativeChange,
	getSchemaAlternatives
} from "./alternatives";
import { FileInputValidation } from "./FileInputValidation";
import type { FileInputState } from "./FileInputValidation";
import { getUiSchema } from "./schema";
import { createHiddenValuesMiddleware } from "./hiddenValuesMiddleware";
import { dataAtPath } from "./values";
import type {
	FormUiSchema,
	JsonSchemaDefinition,
	ValidationError
} from "./types";
import { createFormAjv } from "./validation";
import { formatValidationErrors } from "./validationMessages";
import { VisibilityContext } from "./VisibilityContext";

export type {
	FormMetadata,
	JsonSchemaDefinition,
	FormUiSchema,
	ReferenceOptions,
	ValidationError
} from "./types";

export {
	arrayItemSchema,
	controlPath,
	enumOptions,
	generatedObjectUiSchema,
	generatedUiSchema,
	getUiSchema,
	referenceOptions,
	resolveSchema,
	schemaAtScope,
	schemaType
} from "./schema";

export {
	cloneValue,
	dataAtPath,
	isRecord
} from "./values";

export {
	collectionDraftErrors,
	createFormAjv,
	errorPath,
	errorsAtPath
} from "./validation";

export { elementState } from "./visibility";

export type { AlternativeState } from "./AlternativeContext";

export type {
	AlternativeKind,
	SchemaAlternative,
	SchemaAlternatives
} from "./alternatives";

export {
	dateInputValue,
	dateStoredValue
} from "./dates";

export {
	decodeFileValue,
	encodeFileValue,
	fileSize
} from "./files";

interface FormState extends Omit<JsonFormsCore, "data" | "errors"> {
	data: unknown;
	errors?: ValidationError[];
}

export class FormRuntime {
	public readonly ajv = createFormAjv();

	public readonly ui: FormUiSchema;

	public readonly alternatives: AlternativeContext;

	private readonly fileInputs = new FileInputValidation(this.ajv);

	private state: FormState;

	private validationErrors: ValidationError[] = [];

	private readonly middleware: Middleware;

	private visibilityContext!: VisibilityContext;

	public constructor(
		public readonly schema: JsonSchemaDefinition,
		data: unknown,
		ui?: FormUiSchema,
		alternatives?: AlternativeContext
	) {
		this.ui = ui ?? getUiSchema(schema);
		this.alternatives = alternatives?.clone() ?? new AlternativeContext();

		this.middleware = createHiddenValuesMiddleware(schema, this.ui, this.ajv, this.alternatives, visibility => {
			this.visibilityContext = visibility;
			this.fileInputs.clearHidden(path => visibility.isVisible(path));

			for(const path of visibility.hiddenPaths) {
				this.alternatives.clearSubtree(path);
			}
		});
		this.state = this.reduce(initState, Actions.init(data, schema, this.ui, {
			ajv: this.ajv,
			validationMode: "ValidateAndHide"
		}));
	}

	public get data(): unknown {
		return this.state.data;
	}

	public get errors(): ValidationError[] {
		return this.validationErrors;
	}

	public get inputErrors(): ValidationError[] {
		return this.fileInputs.currentErrors;
	}

	/** Async controls can check ownership even after their field becomes hidden. */
	public hasInputError(error: ValidationError): boolean {
		return this.getInputErrorPath(error) !== undefined;
	}

	public getInputErrorPath(error: ValidationError): string | undefined {
		return this.fileInputs.pathOf(error);
	}

	public setFileInput(path: string, input: FileInputState | undefined, schema: JsonSchemaDefinition): void {
		this.fileInputs.set(path, input, schema);
		this.refresh();
	}

	public clearInputErrors(path: string): void {
		this.fileInputs.clearSubtree(path);
		this.refresh();
	}

	public isVisible(path: string): boolean {
		return this.visibilityContext.isVisible(path);
	}

	public getAlternatives(schema: JsonSchemaDefinition, path: string): AlternativeState | undefined {
		return this.alternatives.get(schema, path, dataAtPath(this.data, path), this.ajv, this.schema);
	}

	public getAlternativeChange(schema: JsonSchemaDefinition, path: string, index: number): ReturnType<typeof getAlternativeChange> {
		const alternatives = getSchemaAlternatives(schema, this.schema);

		if(!alternatives || !alternatives.options[index]) {
			throw new Error("The selected form option does not exist.");
		}

		return getAlternativeChange(alternatives, index, dataAtPath(this.data, path), this.schema);
	}

	public selectAlternative(schema: JsonSchemaDefinition, path: string, index: number): void {
		const alternatives = getSchemaAlternatives(schema, this.schema);

		if(alternatives?.options[index]) {
			const change = getAlternativeChange(alternatives, index, dataAtPath(this.data, path), this.schema);
			this.alternatives.select(schema, path, index, change.replace, this.schema);
			// The following core update commits the selection and value together. Inferring
			// nested editors against the previous value would leave them on a stale branch.
		}
	}

	public update(path: string, value: unknown): void {
		this.reduce(this.state, Actions.update(path, () => value));
	}

	public removeRow(path: string, index: number): void {
		const rows = dataAtPath(this.data, path);

		if(!Array.isArray(rows) || index < 0 || index >= rows.length) {
			return;
		}

		this.fileInputs.removeRow(path, index);
		this.alternatives.removeRow(path, index);
		const next = coreReducer(this.state, Actions.update(path, () => rows.filter((_, row) => row !== index)));
		this.replace(next.data, true);
	}

	public replace(data: unknown, preserveAlternatives = false): void {
		if(!preserveAlternatives) {
			this.alternatives.clear();
			this.fileInputs.clearSubtree("");
		}

		const action = Actions.updateCore(data, this.schema, this.ui, {
			ajv: this.ajv,
			validationMode: this.state.validationMode
		});

		this.reduce(this.state, action);
	}

	/** Use the same data policy in native React bindings and direct AngularJS updates. */
	public reduce(state: JsonFormsCore, action: CoreActions, reduce = coreReducer): JsonFormsCore {
		if(action.type === UPDATE_DATA) {
			this.fileInputs.clearSubtree(action.path);
		}

		const next = this.middleware(state, action, reduce);
		const errors = formatValidationErrors(next.errors ?? [], this.schema);
		const additionalErrors = this.inputErrors;
		this.state = {
			...next,
			errors: isEqual(state.errors, errors) ? state.errors : errors,
			additionalErrors: isEqual(state.additionalErrors, additionalErrors) ? state.additionalErrors : additionalErrors
		};

		const combined = [...errors, ...additionalErrors];

		if(!isEqual(this.validationErrors, combined)) {
			this.validationErrors = combined;
		}

		return this.state;
	}

	private refresh(): void {
		this.reduce(this.state, Actions.updateCore(this.data, this.schema, this.ui, {
			ajv: this.ajv,
			validationMode: this.state.validationMode
		}));
	}
}
